import { JobSource } from '../../shared/interfaces/execution-plan.interfaces';
import { ExecStatus } from '../../shared/interfaces/enums.interface';
import { parseServerDate, toNaiveUtcString, toValidDate } from '../../shared/helpers/date-formatting.helper';

export interface PlanDisplayItem {
  planId: number;
  name: string;
  description?: string;
  runsAsName: string;
  runsAsId: number;
  schedule: string;
  crontab?: string;
  createdAt: string;
  status: 'Active' | 'Paused';
  actions: string[];
  useHead?: boolean;
  workflowId?: number;
  workflowName?: string;
  versionId?: number;
  /** Routine-scoped job arguments as a JSON object string (SCRUM-625). */
  arguments?: string;
}

export interface JobDisplayItem {
  jobId: number;
  planId: number;
  planName: string;
  runsAsName: string;
  source: 'manual' | 'scheduled' | 'webhook' | 'triggered';
  status: string;
  createdAt: string;
  /** Raw ISO timestamp for reliable sorting across routines. */
  rawCreatedAt: string;
  scheduledTime?: string;
  rawScheduledTime?: string;
  startedAt?: string;
  rawStartedAt?: string;
  finishedAt?: string;
  rawFinishedAt?: string;
  duration?: string;
  actions: string[];
  versionId?: number;
  isHeadVersion?: boolean;
}

const CRON_LABELS: Record<string, string> = {
  '0 * * * *': 'Every hour',
  '0 0 * * *': 'Daily at midnight',
  '0 9 * * *': 'Daily at 9 AM',
  '0 9 * * 1': 'Weekly on Monday at 9 AM',
  '0 0 1 * *': 'Monthly on 1st',
  '*/15 * * * *': 'Every 15 minutes',
};

export function formatCronExpression(cron: string): string {
  return CRON_LABELS[cron] ?? cron;
}

export function formatDisplayDate(dateString: string): string {
  const date = toValidDate(dateString);
  if (!date) return '';
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).replace(',', ' at');
}

export function calculateDuration(startedAt?: string | null, finishedAt?: string | null): string | undefined {
  if (!startedAt || !finishedAt) return undefined;

  const start = parseServerDate(startedAt);
  const end = parseServerDate(finishedAt);
  if (!start || !end) return undefined;

  const diff = Math.max(0, end.getTime() - start.getTime());
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export function getStartedSinceIso(timeFilter: string): string | undefined {
  const now = Date.now();
  const offsets: Record<string, number> = {
    'Last 24 hours': 24 * 60 * 60 * 1000,
    'Last 7 days': 7 * 24 * 60 * 60 * 1000,
    'Last 30 days': 30 * 24 * 60 * 60 * 1000,
  };
  const offset = offsets[timeFilter];
  // `startedSince` and the stats `since` are NaiveDateTime on the server, which
  // rejects toISOString()'s trailing Z with a 400 (EHQ-347).
  return offset ? toNaiveUtcString(new Date(now - offset)) : undefined;
}

export function mapToPlanDisplayItem(plan: any): PlanDisplayItem {
  return {
    planId: plan.routineId || plan.planId,
    name: plan.name,
    description: plan.description,
    runsAsName: plan.runsAsName || 'Unknown User',
    runsAsId: plan.runsAsId,
    schedule: plan.crontab ? formatCronExpression(plan.crontab) : 'Manual only',
    crontab: plan.crontab,
    createdAt: formatDisplayDate(plan.createdAt),
    status: plan.paused ? 'Paused' : 'Active',
    actions: ['Edit', 'Delete', 'Run Now', 'Pause/Resume', 'View Jobs', 'View Stats', 'View History'],
    useHead: plan.useHead || false,
    workflowId: plan.workflowId,
    workflowName: plan.workflowName,
    versionId: plan.versionId,
    arguments: plan.arguments,
  };
}

/** API returns ExecStatus enum values (e.g. `completed`); UI filters use Title Case labels. */
export function toDisplayJobStatus(status: string | undefined | null): string {
  if (!status) return '';
  const k = status.toLowerCase();
  const m: Record<string, string> = {
    pending: 'Pending',
    scheduled: 'Scheduled',
    executing: 'Executing',
    needsreview: 'Needs Review',
    canceled: 'Canceled',
    completed: 'Completed',
    failed: 'Failed',
    retry: 'Retry',
    aborted: 'Aborted',
  };
  return m[k] ?? status;
}

/** Map Jobs tab status filter label to API query value. */
export function jobStatusFilterToApi(label: string): ExecStatus | undefined {
  if (label === 'Status') return undefined;
  // Strip spaces so multi-word labels (e.g. 'Needs Review') match the
  // space-free ExecStatus values (e.g. 'needsreview').
  const lower = label.toLowerCase().replace(/\s+/g, '');
  const allowed = new Set<string>(Object.values(ExecStatus));
  return allowed.has(lower) ? (lower as ExecStatus) : undefined;
}

export function mapToJobDisplayItem(
  job: any,
  planName: string,
  runsAsName: string,
  selectedPlanId: number | null,
  currentVersionId: number | null,
): JobDisplayItem {
  const actions: string[] = [];
  const status = toDisplayJobStatus(job.status);

  if (['Executing', 'Needs Review', 'Completed', 'Failed', 'Retry', 'Aborted'].includes(status)) {
    actions.push('View Details', 'View Logs', 'View Files');
  }
  if (['Pending', 'Scheduled'].includes(status)) {
    actions.push('Cancel');
  }
  if (['Failed', 'Canceled', 'Retry', 'Aborted'].includes(status)) {
    actions.push('Retry');
  }
  actions.push('Refresh Status');

  const scheduledTimeStr = job.scheduledTime ?? job.scheduled_time;
  const startedAtStr = job.startedAt ?? job.started_at;
  const finishedAtStr = job.finishedAt ?? job.finished_at;
  const createdAtStr = job.createdAt ?? job.created_at;

  return {
    jobId: job.jobId,
    planId: job.routineId || job.planId || selectedPlanId,
    planName,
    runsAsName,
    source: job.source,
    status,
    createdAt: formatDisplayDate(createdAtStr),
    rawCreatedAt: createdAtStr ?? '',
    scheduledTime: scheduledTimeStr ? formatDisplayDate(scheduledTimeStr) : undefined,
    rawScheduledTime: scheduledTimeStr ?? '',
    startedAt: startedAtStr ? formatDisplayDate(startedAtStr) : undefined,
    rawStartedAt: startedAtStr ?? '',
    finishedAt: finishedAtStr ? formatDisplayDate(finishedAtStr) : undefined,
    rawFinishedAt: finishedAtStr ?? '',
    duration: calculateDuration(startedAtStr, finishedAtStr),
    actions,
    versionId: job.versionId,
    isHeadVersion: currentVersionId != null && job.versionId === currentVersionId,
  };
}

export function getStatusClass(status: string): string {
  switch (status) {
    case 'Active':
    case 'Executing':
    case 'Scheduled':
      return 'status-active';
    case 'Paused':
    case 'Pending':
    case 'Needs Review':
      return 'status-pending';
    case 'Failed':
    case 'Canceled':
      return 'status-failed';
    case 'Completed':
      return 'status-success';
    case 'Retry':
      return 'status-retry';
    case 'Aborted':
      return 'status-aborted';
    default:
      return '';
  }
}

export function getStatusIcon(status: string): string {
  switch (status) {
    case 'Active':
    case 'Executing':
      return 'play_circle';
    case 'Paused':
    case 'Pending':
      return 'pause_circle';
    case 'Scheduled':
      return 'schedule';
    case 'Needs Review':
      return 'rate_review';
    case 'Failed':
    case 'Canceled':
      return 'error';
    case 'Completed':
      return 'check_circle';
    case 'Retry':
      return 'replay';
    case 'Aborted':
      return 'block';
    default:
      return 'help';
  }
}

export function getActionIcon(action: string): string {
  const icons: Record<string, string> = {
    'Edit': 'edit',
    'Delete': 'delete',
    'Run Now': 'play_arrow',
    'Pause/Resume': 'pause_circle',
    'View Jobs': 'list',
    'View Details': 'info',
    'View Logs': 'description',
    'View Files': 'folder',
    'Cancel': 'cancel',
    'Retry': 'replay',
    'Refresh Status': 'refresh',
    'View Stats': 'insights',
    'View History': 'history',
  };
  return icons[action] ?? 'more_horiz';
}

export function formatJobLogs(steps: any[]): string {
  if (steps.length === 0) return 'No execution steps found.';

  return steps.map((step, index) => {
    const lines = [
      `=== Step ${index + 1} (ID: ${step.stepId}) ===`,
      `Status: ${step.status}`,
      `Started: ${toValidDate(step.startedAt)?.toLocaleString() ?? 'Not started'}`,
      `Finished: ${toValidDate(step.finishedAt)?.toLocaleString() ?? 'Not finished'}`,
    ];
    if (step.logs) lines.push(`\nLogs:`, step.logs);
    if (step.messages) lines.push(`\nMessages:`, step.messages);
    if (step.results) lines.push(`\nResults:`, step.results);
    if (step.renders) lines.push(`\nRenders:`, step.renders);
    if (step.envDiff) lines.push(`\nEnvironment Changes:`, step.envDiff);
    return lines.join('\n');
  }).join('\n\n');
}

export function buildFallbackExecPlan(plan: PlanDisplayItem, projectId: number): any {
  return {
    routineId: plan.planId,
    userId: 0,
    projectId,
    runsAsId: plan.runsAsId,
    paused: plan.status === 'Paused',
    source: plan.crontab ? JobSource.Scheduled : JobSource.Manual,
    createdAt: new Date().toISOString(),
    crontab: plan.crontab,
    name: plan.name,
    description: plan.description,
    versionId: 0,
    runsAsName: plan.runsAsName,
  };
}
